// Schéma de validation d'un signalement (zod 4) — miroir en runtime des types
// TS de fil.ts (FilEvent), contexte.ts (Contexte) et du DeviceInfo natif
// (Task A2, src/lib/native/diagnostics.ts). Utilisé côté app avant l'envoi et
// côté Edge Function (Task B3) pour rejeter tôt un signalement malformé.

import { z } from "zod";
// Valeurs sans zod (SignalerProbleme les lit de façon SYNCHRONE — le fieldset
// des 3 choix est toujours affiché à l'ouverture — hors du chemin d'envoi qui,
// lui, charge zod à la demande). Ré-exportées ici pour ne rien casser des
// imports existants (`import { GRAVITES } from "./schema"`).
import { GRAVITE_LIBELLES, GRAVITES, type Gravite } from "./gravites";

export type { Gravite };
export { GRAVITE_LIBELLES, GRAVITES };

export const contexteSchema = z.object({
  url: z.string(),
  chemin: z.string(),
  navigateur: z.string(),
  agent: z.string(),
  langue: z.string(),
  ecran: z.string(),
  fenetre: z.string(),
  pixelRatio: z.number(),
  enLigne: z.boolean(),
  heure: z.string(),
  fuseau: z.string(),
  version: z.string(),
  natif: z.boolean(),
});

export const deviceInfoSchema = z.object({
  platform: z.enum(["ios", "web"]),
  model: z.string(),
  osVersion: z.string(),
  appVersion: z.string(),
  appBuild: z.string(),
});

export const filEventSchema = z.discriminatedUnion("kind", [
  z.object({ t: z.string(), kind: z.literal("page"), path: z.string() }),
  z.object({ t: z.string(), kind: z.literal("clic"), cible: z.string() }),
  z.object({
    t: z.string(),
    kind: z.literal("soumission"),
    formulaire: z.string(),
    champs: z.array(z.string()),
  }),
  z.object({
    t: z.string(),
    kind: z.literal("api"),
    methode: z.string(),
    chemin: z.string(),
    statut: z.union([z.number(), z.literal("reseau")]),
    dureeMs: z.number(),
    fois: z.number().int().positive().optional(),
  }),
  z.object({ t: z.string(), kind: z.literal("erreur"), message: z.string() }),
]);

export const signalementSchema = z.object({
  numero: z.string().regex(/^SIG-\d{6}-\d{6}$/),
  description: z.string().min(10).max(4000),
  gravite: z.enum(GRAVITES),
  contexte: contexteSchema,
  appareil: deviceInfoSchema,
  fil: z.array(filEventSchema).max(30),
  diagnostics: z.unknown().nullable(),
  erreur: z
    .object({
      message: z.string().max(300),
      pile: z.string().max(4000).nullable(),
    })
    .nullable(),
});

export type Signalement = z.infer<typeof signalementSchema>;
